import React, { useCallback } from 'react';
import { css, Theme, useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { clamp } from 'lodash';

import { useAppState } from "core/contexts/AppContext";
import useCart from '../CartPage/hooks/useCart';
import useChevron from "core/hooks/useChevron";
import useSetState from "core/hooks/useSetState";
import RoundChevronButton from "core/Components/Buttons/RoundChevronButton";
import Gallery from "core/Components/Item/Gallery";
import ItemChevron from "core/Components/Item/ItemChevron";
import ItemSection from "core/Components/Item/ItemSection";
import ItemWrapper from "core/Components/Item/ItemWrapper";
import LeftContainer from "core/Components/Item/LeftContainer";
import MainHeading from "core/Components/Item/MainHeading";
import RightContainer from "core/Components/Item/RightContainer";
import SectionContainer from "core/Components/SectionContainer";
import VideoPlayer from "core/Components/Video/VideoPlayer";
import VideoViewContainer from "core/Components/Video/VideoViewContainer";
import VideoViewLayer from "core/Components/Video/VideoViewLayer";
import ItemAddToCartButton from "pages/Components/ProductPage/ItemAddToCartButton";
import ItemDescription from "pages/Components/ProductPage/ItemDescription";
import ItemOutOfStock from "pages/Components/ProductPage/ItemOutOfStock";
import ProductPrice from "pages/Components/ProductPage/ItemPriceBox";
import ItemQuantityBox from "pages/Components/ProductPage/ItemQuantityBox";
import { getProductFromCart } from 'pages/includes/cart';
import { getDeliveryCountries, MAXIMUM_PRODUCT_QUANTITY, MINIMUM_PRODUCT_QUANTITY } from '../ProductListPage/includes/includes';
import RedirectPage from 'pages/RedirectPage';
import Description from "pages/Components/CartAndCheckout/components/Description";
import ConfirmationModal from "pages/Components/CartAndCheckout/components/ConfirmationModal";

type DefaultState = {
    product?: ProductPage | null,
    quantity: number,
    videoPath: string,
    isProcessing: boolean,
    showConfirmationCartModal: boolean,
};

const INITIAL_STATE = {
    quantity: 1,
    videoPath: '',
    isProcessing: false,
    showConfirmationCartModal: false,
};

type Props = {
    data: ProductItem,
};

const ProductPage: React.FC<Props> = ({ data }) => {
    const theme: Theme = useTheme();
    const navigate = useNavigate();

    const [appState] = useAppState();
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);

    const { updateCart } = useCart();
    const { chevronSize, chevronColour, chevronBackground, chevronBackgroundHover } = useChevron();

    const product = data;
    const productInCart = getProductFromCart(appState.cart, parseInt(product?.id));
    const productMaximumQuantity = Math.min(
        MAXIMUM_PRODUCT_QUANTITY,
        (product?.inventoryQuantity ?? 0) - (productInCart?.quantity ?? 0),
    );
    const productDeliveryCountries = getDeliveryCountries(product);
    const isProductOutOfStock =
        !product ||
        !!product.outOfStock ||
        !product.availableForSale ||
        (!!product.inventoryQuantity && product.inventoryQuantity <= 0)
    ;
    const isProductAddableToCart =
        !isProductOutOfStock &&
        !!product.inventoryQuantity &&
        state.quantity <= product.inventoryQuantity
    ;

    const onShowVideoClick = (videoPath: string) => setState({ videoPath });
    const onCloseVideoClick = () => setState({ videoPath: '' });

    // Set the product quantity start -----------------------------------
    const getUpdatedQuantity = (newQuantity: number) => (
        clamp(
            newQuantity,
            MINIMUM_PRODUCT_QUANTITY,
            productMaximumQuantity,
        )
    );

    // Plus product quantity
    const onPlusIconClick = () => {
        setState((prev: DefaultState) => ({ quantity: getUpdatedQuantity(prev.quantity + 1) }));
    };

    // Minus product quantity
    const onMinusIconClick = () => {
        setState((prev: DefaultState) => ({ quantity: getUpdatedQuantity(prev.quantity - 1) }));
    };

    // Set custom product quantity
    // const onCustomQuantity = (id: string, quantityElement: ChangeEvent<HTMLInputElement>) => {
    const onCustomQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Allow to input number only
        let inputQuantity = parseInt(e.target.value.replace(/\D/g, ''));
        if (isNaN(inputQuantity)) inputQuantity = MINIMUM_PRODUCT_QUANTITY;
        setState({ quantity: getUpdatedQuantity(inputQuantity) });
    };
    // Set the product quantity end -------------------------------------

    // Add to cart (1) Make the default quantity (2) Close the cart popup
    const addToCartHandler = () => {
        if (appState.cart && appState.cart.countryCode !== appState.countryCode.code) {
            setState({ showConfirmationCartModal: true });
        } else {
            setState({ isProcessing: true });
            updateCart({
                productId: parseInt(product.id),
                productQuantity: (productInCart?.quantity ?? 0) + state.quantity
            });
        }
    };

    const closeCountryCheckoutPopup = useCallback(() => {
        setState({ showConfirmationCartModal: false });
    }, [setState]);

    const onCart = useCallback(() => {
        closeCountryCheckoutPopup();
        navigate('/cart-summary')
    }, [closeCountryCheckoutPopup, navigate]);


    // Redirect user if the product is not available in their country
    if (!productDeliveryCountries.includes(appState.countryCode.code.toLowerCase())) {
        return <RedirectPage url={'/classroom-resources'} />
    }

    const galleryStyle = css`
        border: 1px solid ${theme.colours.alto};
    `;

    const countryConfirmationModalStyle = css`
        .confirmation-modal-content {
            margin: auto 0;
        }
    `;

    return (
        <SectionContainer>
            {product && (
                <>
                    <ItemChevron chevronSize={chevronSize}>
                        <RoundChevronButton
                            type={"left"}
                            onClick={() => navigate(-1)}
                            roundColour={chevronBackground}
                            roundHoverColour={chevronBackgroundHover}
                            chevronColour={chevronColour}
                            size={chevronSize}
                        />
                    </ItemChevron>

                    <ItemWrapper>
                        <ItemSection fontColour={"black"}>
                            <LeftContainer>
                                {product.imagesAndVideos && (
                                    <Gallery
                                        css={galleryStyle}
                                        imagesAndVideos={product.imagesAndVideos}
                                        onVideoClick={onShowVideoClick}
                                    />
                                )}
                            </LeftContainer>
                        </ItemSection>
                        <ItemSection fontColour={theme.colours.white} backgroundColour={theme.colours.curiousBlue2}>
                            <RightContainer>
                                <MainHeading text={product.title} />
                                <ProductPrice
                                    price={product.price.amount}
                                    discountPrice={product.price.discountedPrice}
                                    shouldDiscount={product.hasOnGoingDiscount && !!product.price.discountedPrice}
                                    isTaxable
                                />
                                {isProductAddableToCart && (
                                    <ItemQuantityBox
                                        quantity={state.quantity}
                                        maxQuantity={productMaximumQuantity}
                                        onPlusIconClick={onPlusIconClick}
                                        onMinusIconClick={onMinusIconClick}
                                        onCustomQuantity={onCustomQuantity}
                                    />
                                )}
                                {isProductOutOfStock && <ItemOutOfStock />}
                                <ItemDescription>
                                    {product.description}
                                </ItemDescription>
                                <ItemAddToCartButton
                                    addToCartHandler={addToCartHandler}
                                    disabled={!isProductAddableToCart}
                                />
                                 <Description marginTop={30}>{product.productDisclaimer?.value ?? ''}</Description>
                            </RightContainer>
                        </ItemSection>
                    </ItemWrapper>
                </>
            )}

            {state.videoPath && (
                <VideoViewContainer clearPlayVideo={onCloseVideoClick}>
                    <VideoViewLayer>
                        <VideoPlayer videoPath={state.videoPath} autoPlay />
                    </VideoViewLayer>
                </VideoViewContainer>
            )}
            {state.showConfirmationCartModal && (
                <ConfirmationModal
                    message={
                        <>
                            <div>
                                Oops! &nbsp;This product doesn’t match your region.
                            </div>
                            <br/>
                            <div>
                                Please check out your cart first, then change country to browse products in your region.
                            </div>
                        </>
                    }
                    confirmLabel={"Go to cart"}
                    onCancel={closeCountryCheckoutPopup}
                    onConfirm={onCart}
                    css={countryConfirmationModalStyle}
                />
            )}
        </SectionContainer>
    );
};

export default ProductPage;